import { translateInComponent } from 'infras/i18n';
import translations from './i18n.json';
import { SyntheticEvent, useMemo } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import {
  spacing,
  mediaMax,
  fontSize,
  borderRadius,
} from 'presentations/views/styles';
import Skeleton from 'presentations/views/components/atoms/Skeleton';
import Workflow from 'domains/models/workflow';
import { WorkflowType } from 'domains/models/workflow/workflowType';
import { ErrorList } from 'domains/models';
import TextField from 'presentations/views/components/atoms/TextField';
import TextArea from 'presentations/views/components/atoms/TextArea';
import FormLabel from 'presentations/views/components/molecules/FormLabel';
import Checkbox from 'presentations/views/components/atoms/Checkbox';
import Radio from 'presentations/views/components/atoms/Radio';
import Select from 'presentations/views/components/atoms/Select';
import ErrorText from 'presentations/views/components/atoms/ErrorText';
import RichReadView from 'presentations/views/components/atoms/RichReadView';
import {
  blue01,
  blue02,
  blue03,
  gray04,
  primary,
} from 'presentations/views/styles/color';
import { convertHexToRGBA } from 'presentations/views/helpers/color';
import { otherSelectValue } from 'domains/models/answer';
import { ViewerItem } from 'presentations/api/schema/user';

export interface Props {
  viewer: ViewerItem | null;
  question: Workflow;
  onChange?: any;
  onChangeOtherAnswer?: any;
  index: number;
  currentIndex: number;
  customQuestionAnswers: any[];
  customOtherAnswers: any[];
  errors: ErrorList;
  isFullWidth: boolean;
  isColored: boolean;
  color?: string;
  email?: string | null;
  name?: string | null;
  phoneNumber?: string | null;
}

export default function Component(props: Props) {
  const t = translateInComponent(translations);

  const onOuterClick = (e: SyntheticEvent<HTMLDivElement>) => {
    props.onChange(e);
  };

  const fieldProps = {
    isColored: props.isColored,
    isFullWidth: props.isFullWidth,
  };
  const isAutoFocus = props.index !== 0 && props.index === props.currentIndex;

  switch (props.question.workflowType) {
    case WorkflowType.QuestionAnswerEmail:
      return (
        <ContentField {...fieldProps}>
          <FormLabel
            isRequired={props.question.isRequired}
            isNotRequired={!props.question.isRequired}
          >
            <TitleLabel>{props.question.content}</TitleLabel>
          </FormLabel>
          <TextField
            isAutoFocus={isAutoFocus}
            isRequired={props.question.isRequired}
            isNotRequired={!props.question.isRequired}
            isFullWidth
            type="email"
            name={`customQuestion.${props.index}`}
            value={props.customQuestionAnswers[props.index]}
            variant={props.isColored ? 'standard' : 'outlined'}
            placeholder={t('email placeholder')}
            onChange={props.onChange}
            errors={props.errors[`customQuestion.${props.index}`]}
            outlineColor={props.color}
          />
        </ContentField>
      );
    case WorkflowType.QuestionAnswerName:
      return (
        <ContentField {...fieldProps}>
          <FormLabel
            isRequired={props.question.isRequired}
            isNotRequired={!props.question.isRequired}
          >
            <TitleLabel>{props.question.content}</TitleLabel>
          </FormLabel>
          <TextField
            isAutoFocus={isAutoFocus}
            isRequired={props.question.isRequired}
            isNotRequired={!props.question.isRequired}
            isFullWidth
            name={`customQuestion.${props.index}`}
            value={props.customQuestionAnswers[props.index]}
            variant={props.isColored ? 'standard' : 'outlined'}
            placeholder={t('name placeholder')}
            onChange={props.onChange}
            errors={props.errors[`customQuestion.${props.index}`]}
            outlineColor={props.color}
          />
        </ContentField>
      );
    case WorkflowType.QuestionAnswerPhoneNumber:
      return (
        <ContentField {...fieldProps}>
          <FormLabel
            isRequired={props.question.isRequired}
            isNotRequired={!props.question.isRequired}
          >
            <TitleLabel>{props.question.content}</TitleLabel>
          </FormLabel>
          <TextField
            isAutoFocus={isAutoFocus}
            isRequired={props.question.isRequired}
            isNotRequired={!props.question.isRequired}
            isFullWidth
            name={`customQuestion.${props.index}`}
            value={props.customQuestionAnswers[props.index]}
            type="tel"
            variant={props.isColored ? 'standard' : 'outlined'}
            placeholder={t('phoneNumber placeholder')}
            onChange={props.onChange}
            errors={props.errors[`customQuestion.${props.index}`]}
            outlineColor={props.color}
          />
        </ContentField>
      );
    case WorkflowType.QuestionAnswerHostInsertText:
      return (
        <ContentField {...fieldProps}>
          <RichReadView content={props.question.content} />
        </ContentField>
      );
    case WorkflowType.QuestionAnswerText:
      return (
        <ContentField {...fieldProps}>
          <FormLabel
            isRequired={props.question.isRequired}
            isNotRequired={!props.question.isRequired}
          >
            <TitleLabel>{props.question.content}</TitleLabel>
          </FormLabel>
          <TextField
            isRequired={props.question.isRequired}
            isNotRequired={!props.question.isRequired}
            isAutoFocus={isAutoFocus}
            isFullWidth
            name={`customQuestion.${props.index}`}
            value={props.customQuestionAnswers[props.index]}
            variant={props.isColored ? 'standard' : 'outlined'}
            placeholder={t('custom placeholder')}
            onChange={props.onChange}
            errors={props.errors[`customQuestion.${props.index}`]}
            outlineColor={props.color}
          />
        </ContentField>
      );
    case WorkflowType.QuestionAnswerMultilineText:
      return (
        <ContentField {...fieldProps}>
          <FormLabel
            isRequired={props.question.isRequired}
            isNotRequired={!props.question.isRequired}
          >
            <TitleLabel>{props.question.content}</TitleLabel>
          </FormLabel>
          <TextArea
            isRequired={props.question.isRequired}
            isNotRequired={!props.question.isRequired}
            isAutoFocus={isAutoFocus}
            isFullWidth
            name={`customQuestion.${props.index}`}
            value={props.customQuestionAnswers[props.index]}
            placeholder={t('custom placeholder')}
            onChange={props.onChange}
            errors={props.errors[`customQuestion.${props.index}`]}
            outlineColor={props.color}
          />
        </ContentField>
      );
    case WorkflowType.QuestionAnswerCheckboxes:
      return (
        <ContentField {...fieldProps}>
          <FormLabel
            isRequired={props.question.isRequired}
            isNotRequired={!props.question.isRequired}
          >
            <TitleLabel>{props.question.content}</TitleLabel>
          </FormLabel>
          <SelectList isColored={props.isColored}>
            {props.question.choices &&
              props.question.choices.map((choice) => (
                <SelectListItem key={choice.uid} isColored={props.isColored}>
                  <ColoredBox
                    isColored={props.isColored}
                    color={props.color}
                    data-name={`customQuestion.${props.index}`}
                    data-value={choice.uid}
                    onClick={onOuterClick}
                  >
                    <Checkbox
                      name={`customQuestion.${props.index}`}
                      value={choice.uid}
                      // onChange={props.onChange}
                      specifyColor={props.color}
                      isChecked={
                        typeof props.customQuestionAnswers[props.index] ===
                          'object' &&
                        props.customQuestionAnswers[props.index].includes(
                          choice.uid,
                        )
                      }
                      label={choice.value}
                    />
                  </ColoredBox>
                </SelectListItem>
              ))}
            {props.question.hasOtherChoice && (
              <SelectListItem isColored={props.isColored}>
                <ColoredBox
                  isColored={props.isColored}
                  color={props.color}
                  data-name={`customQuestion.${props.index}`}
                  data-value={otherSelectValue}
                  onClick={onOuterClick}
                >
                  <Checkbox
                    name={`customQuestion.${props.index}`}
                    value={otherSelectValue}
                    // onChange={props.onChange}
                    specifyColor={props.color}
                    isChecked={
                      typeof props.customQuestionAnswers[props.index] ===
                        'object' &&
                      props.customQuestionAnswers[props.index].includes(
                        otherSelectValue,
                      )
                    }
                    label={t(otherSelectValue)}
                  />
                </ColoredBox>
              </SelectListItem>
            )}
          </SelectList>
          {props.question.hasOtherChoice && (
            <TextArea
              isFullWidth
              name={`customQuestion.${props.index}`}
              placeholder={t('custom other choice placeholder')}
              value={props.customOtherAnswers[props.index]}
              onChange={props.onChangeOtherAnswer}
              outlineColor={props.color}
            />
          )}
          {props.errors[`customQuestion.${props.index}`] &&
            props.errors[
              `customQuestion.${props.index}`
            ].map((errorText, i) => <ErrorText key={i}>{errorText}</ErrorText>)}
        </ContentField>
      );
    case WorkflowType.QuestionAnswerMultipleChoice:
      return (
        <ContentField {...fieldProps}>
          <FormLabel
            isRequired={props.question.isRequired}
            isNotRequired={!props.question.isRequired}
          >
            <TitleLabel>{props.question.content}</TitleLabel>
          </FormLabel>
          <SelectList isColored={props.isColored}>
            {props.question.choices &&
              props.question.choices.map((choice) => (
                <SelectListItem key={choice.uid} isColored={props.isColored}>
                  <ColoredBox
                    isColored={props.isColored}
                    color={props.color}
                    data-name={`customQuestion.${props.index}`}
                    data-value={choice.uid}
                    onClick={onOuterClick}
                  >
                    <Radio
                      name={`customQuestion.${props.index}`}
                      isChecked={
                        props.customQuestionAnswers[props.index] === choice.uid
                      }
                      label={choice.value}
                      onChange={props.onChange}
                      value={choice.uid}
                      outlineColor={props.color}
                    />
                  </ColoredBox>
                </SelectListItem>
              ))}
            {props.question.hasOtherChoice && (
              <>
                <SelectListItem isColored={props.isColored}>
                  <ColoredBox
                    isColored={props.isColored}
                    color={props.color}
                    data-name={`customQuestion.${props.index}`}
                    data-value={otherSelectValue}
                    onClick={onOuterClick}
                  >
                    <Radio
                      name={`customQuestion.${props.index}`}
                      isChecked={
                        props.customQuestionAnswers[props.index] ===
                        otherSelectValue
                      }
                      label={t(otherSelectValue)}
                      onChange={props.onChange}
                      value={otherSelectValue}
                    />
                  </ColoredBox>
                </SelectListItem>
              </>
            )}
          </SelectList>
          {props.question.hasOtherChoice && (
            <TextArea
              isFullWidth
              name={`customQuestion.${props.index}`}
              value={props.customOtherAnswers[props.index]}
              placeholder={t('custom other choice placeholder')}
              onChange={props.onChangeOtherAnswer}
              outlineColor={props.color}
            />
          )}
          {props.errors[`customQuestion.${props.index}`] &&
            props.errors[
              `customQuestion.${props.index}`
            ].map((errorText, i) => <ErrorText key={i}>{errorText}</ErrorText>)}
        </ContentField>
      );
    case WorkflowType.QuestionAnswerDropDown:
      const options: any = [{ text: t('select'), value: '' }];

      props.question.choices?.forEach((c) =>
        options.push({ text: c.value, value: c.uid }),
      );
      if (props.question.hasOtherChoice) {
        options.push({ text: t(otherSelectValue), value: otherSelectValue });
      }
      return (
        <ContentField {...fieldProps}>
          <FormLabel
            isRequired={props.question.isRequired}
            isNotRequired={!props.question.isRequired}
          >
            <TitleLabel>{props.question.content}</TitleLabel>
          </FormLabel>
          <SelectWrap
            name={`customQuestion.${props.index}`}
            value={props.customQuestionAnswers[props.index]}
            variant={props.isColored ? 'standard' : 'outlined'}
            onChange={props.onChange}
            outlineColor={props.color}
            isFullWidth
            options={options}
          />
          {props.question.hasOtherChoice &&
            props.customQuestionAnswers[props.index] === otherSelectValue && (
              <TextArea
                isFullWidth
                name={`customQuestion.${props.index}`}
                value={props.customOtherAnswers[props.index]}
                placeholder={t('custom other choice placeholder')}
                onChange={props.onChangeOtherAnswer}
                outlineColor={props.color}
              />
            )}
          {props.errors[`customQuestion.${props.index}`] &&
            props.errors[
              `customQuestion.${props.index}`
            ].map((errorText, i) => <ErrorText key={i}>{errorText}</ErrorText>)}
        </ContentField>
      );
    default:
      return null;
  }
}

export function LoadingComponent(props: {
  isColored: boolean;
  isFullWidth: boolean;
}) {
  return (
    <ContentField {...props}>
      <FormLabel>
        <Skeleton variant="text" />
      </FormLabel>
      <Skeleton variant="rectangular" width={200} />
    </ContentField>
  );
}

export const ContentField = styled.div<{
  isColored?: boolean;
  isFullWidth?: boolean;
}>`
  ${(props) =>
    props.isColored &&
    css`
      border: 1px solid ${gray04(props)};
      padding: ${spacing(28)};
      border-radius: ${borderRadius(16)};
    `}

  ${(props) =>
    props.isFullWidth &&
    css`
      border: 0;
      padding: 0;
    `}
`;
const SelectList = styled.div<{ isColored: boolean }>`
  ${(props) =>
    props.isColored
      ? css`
          display: flex;
          flex-wrap: wrap;
        `
      : css`
          display: flex;
          flex-wrap: wrap;
          align-items: center;
        `}
`;

const SelectListItem = styled.div<{ isColored: boolean }>`
  ${(props) =>
    props.isColored
      ? css`
          padding-right: ${spacing(20)};
          margin-bottom: ${spacing(20)};
          width: 50%;
          ${mediaMax.tablet} {
            width: 100%;
            padding-right: 0;
          }
        `
      : css`
          margin-right: ${spacing(20)};
        `}
`;

const SelectWrap = styled(Select)`
  width: 100%;
  margin-bottom: ${spacing(8)};
  .MuiSelect-select {
    font-size: ${fontSize(14)};
  }
`;

const ColoredBox = styled.div<{ isColored: boolean; color?: string }>`
  ${(props) =>
    props.isColored &&
    css`
      cursor: pointer;
      padding: ${spacing(8)};
      background-color: ${props.color
        ? convertHexToRGBA(props.color, 10)
        : blue02(props)};
      border-radius: ${borderRadius(6)};
      border: 1px solid ${props.color || primary(props)};
      width: 100%;
      :hover {
        background-color: ${props.color
          ? convertHexToRGBA(props.color, 20)
          : blue03(props)};
      }
    `};
`;

const TitleLabel = styled.span`
  font-size: ${fontSize(16)};
  vertical-align: middle;
`;
